import type { ReactNode } from "react";

import { useNavigate, useRouter } from "@tanstack/react-router";
import { ChevronLeftIcon } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { PlusIcon } from "@/components/icons.tsx";
import { Loader } from "@/components/Loader";
import { SearchField, SearchInput } from "@/components/search-field";
import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";

import type { ActionButtonConfig, FeaturePageLayoutProps } from "./types";

function BackButton() {
  const { history } = useRouter();

  return (
    <Button
      color="gray"
      isIconOnly
      onPress={() => history.go(-1)}
      size="lg"
      variant="secondary"
    >
      <ChevronLeftIcon size={20} />
    </Button>
  );
}

function Header({
  backButton,
  createButton,
  headerContent,
  title,
}: {
  backButton: boolean;
  createButton?: FeaturePageLayoutProps["createButton"];
  headerContent?: ReactNode;
  title: string;
}) {
  const { hasRole } = useRoleAccess();
  const navigate = useNavigate();

  return (
    <div className="rounded-t-xl bg-bg-primary px-xl pb-lg pt-sm lg:px-3xl lg:pb-2xl lg:pt-xl border-b border-b-border-secondary w-full">
      <div className="flex items-center gap-xl lg:gap-2xl min-h-[44px]">
        {backButton && <BackButton />}
        <span className="min-w-0 flex-1 truncate text-2xl font-semibold text-text-primary lg:flex-none lg:overflow-visible lg:text-clip">
          {title}
        </span>

        <div className="hidden lg:flex flex-1 min-w-0 items-center">
          {headerContent}
        </div>

        <div className="flex-shrink-0">
          {createButton && hasRole(createButton.requiredRoles) && (
            <Button
              isDisabled={createButton.isDisabled}
              onPress={
                createButton.onClick ??
                (() => {
                  navigate({ href: createButton.href || "/" });
                })
              }
            >
              <AccessibleIcon>
                <PlusIcon />
              </AccessibleIcon>
              {createButton.label}
            </Button>
          )}
        </div>
      </div>

      {headerContent && (
        <div className="flex lg:hidden items-center flex-wrap gap-1 mt-2">
          {headerContent}
        </div>
      )}
    </div>
  );
}

function SearchBar({
  className,
  inputValue,
  setInputValue,
}: {
  className?: string;
  inputValue: string;
  setInputValue: (value: string) => void;
}) {
  return (
    <SearchField aria-label="Search field" className={className}>
      <SearchInput
        onChange={(e) => setInputValue(e.target.value)}
        onClear={() => setInputValue("")}
        placeholder="Search"
        value={inputValue}
      />
    </SearchField>
  );
}

function ActionButtons({
  buttons,
  className,
}: {
  buttons: ActionButtonConfig[];
  className?: string;
}) {
  const { hasRole } = useRoleAccess();

  const visibleButtons = buttons.filter((button) =>
    hasRole(button.requiredRoles),
  );

  if (visibleButtons.length === 0) return null;

  return (
    <div className={className}>
      <div className="flex gap-3">
        {visibleButtons.map((button, index) => (
          <Button
            isDisabled={button.loading}
            key={index}
            onPress={button.onPress}
            variant={button.variant || "primary"}
          >
            {button.loading ? (
              <Loader className="h-4 w-4" />
            ) : (
              <AccessibleIcon>{button.icon}</AccessibleIcon>
            )}
            {button.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function SearchAndActions({
  actionButtons,
  searchBar,
}: {
  actionButtons?: ActionButtonConfig[];
  searchBar?: {
    inputValue: string;
    setInputValue: (value: string) => void;
  };
}) {
  if (!searchBar && (!actionButtons || actionButtons.length === 0)) {
    return null;
  }

  return (
    <div className="w-full bg-bg-primary pt-6 pb-4">
      <div className="flex items-center gap-4 px-xl">
        {searchBar && (
          <SearchBar
            className="flex-1"
            inputValue={searchBar.inputValue}
            setInputValue={searchBar.setInputValue}
          />
        )}
        {actionButtons && actionButtons.length > 0 && (
          <ActionButtons buttons={actionButtons} className="flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

export function FeaturePageLayout({
  actionButtons,
  backButton = false,
  children,
  createButton,
  headerContent,
  isLoading = false,
  searchBar,
  tabs,
  title,
}: FeaturePageLayoutProps) {
  return (
    <div className="mx-auto bg-bg-tertiary">
      <div className="mx-auto flex w-full flex-col @container">
        <Header
          backButton={backButton}
          createButton={createButton}
          headerContent={headerContent}
          title={title}
        />

        {tabs ? (
          <Tabs
            className={`${tabs.className} rounded-b-xl`}
            onSelectionChange={tabs.onSelectionChange}
            selectedKey={tabs.selectedKey}
          >
            <div className="py-sm lg:py-lg">
              <TabList
                aria-label={`${title} Management`}
                className="px-xl lg:px-2xl flex overflow-x-auto"
              >
                {tabs.tabs.map((tab) => (
                  <Tab id={tab.id} key={tab.id}>
                    {tab.label}
                    {(tab.countLabel !== undefined || tab.isLoading) && (
                      <Badge
                        className={`${
                          tab.id === tabs.selectedKey
                            ? ""
                            : "text-text-tertiary"
                        }`}
                        isRounded
                        variant={
                          tab.id === tabs.selectedKey ? "brand" : "secondary"
                        }
                      >
                        {tab.isLoading ? (
                          <span className="inline-block w-3 h-3 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                        ) : (
                          tab.countLabel
                        )}
                      </Badge>
                    )}
                  </Tab>
                ))}
              </TabList>
            </div>

            <SearchAndActions
              actionButtons={actionButtons}
              searchBar={searchBar}
            />

            {isLoading ? (
              <Loader className="py-20" />
            ) : (
              <>
                {tabs.tabs.map((tab) => (
                  <TabPanel
                    className={`pb-0 ${tab.className}`}
                    id={tab.id}
                    key={tab.id}
                  >
                    {children}
                  </TabPanel>
                ))}
              </>
            )}
          </Tabs>
        ) : (
          <>
            <SearchAndActions
              actionButtons={actionButtons}
              searchBar={searchBar}
            />

            {isLoading ? <Loader className="py-20" /> : children}
          </>
        )}
      </div>
    </div>
  );
}
